import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Wallet,
  Loader2,
  AlertTriangle,
  TrendingUp,
  CalendarClock,
  ReceiptText,
  FileDown,
  Layers,
  ChevronDown,
  ChevronRight,
  Coins,
  Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import AutoRecharge from "@/components/AutoRecharge";
import {
  creditsStatus,
  creditsCheckout,
  creditsSession,
  creditsSetCountry,
  creditsTransactions,
  creditsTransactionsCsv,
  creditsInvoicePdf,
  autoRechargeConfirm,
  downloadBlob,
  filenameFromResponse,
} from "@/lib/api";
import { toast } from "sonner";

const COUNTRY_ORDER = ["AT", "DE", "CH"];
const TX_LABEL = { topup: "Aufladung", auto_topup: "Auto-Aufladung", debit: "Abbuchung" };

const eur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(n) || 0
  );

const credits = (n) => {
  const v = Number(n) || 0;
  const s = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(v);
  return `${s} Credits`;
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function CollapsibleCard({
  icon: Icon,
  title,
  titleRight,
  testid,
  defaultOpen = false,
  headerRight,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-border bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-left flex-1 min-w-0 hover:opacity-80 transition-opacity"
          data-testid={`${testid}-toggle`}
        >
          <Icon className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0 flex items-center gap-2">
            <h2 className="font-medium shrink-0">{title}</h2>
            {titleRight && (
              <span className="text-sm text-muted-foreground truncate">{titleRight}</span>
            )}
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
          )}
        </button>
        {headerRight}
      </div>
      {open && <div className="border-t border-border p-4 sm:p-6">{children}</div>}
    </div>
  );
}

function StatusBadge({ state }) {
  const map = {
    trial: { label: "Testphase", cls: "bg-primary/10 text-primary" },
    active: { label: "Aktiv", cls: "bg-emerald-100 text-emerald-700" },
    blocked: { label: "Gesperrt", cls: "bg-destructive/10 text-destructive" },
    exempt: { label: "Ausgenommen", cls: "bg-muted text-muted-foreground" },
  };
  const s = map[state] || map.active;
  return (
    <span
      className={`text-xs font-semibold px-3 py-1 rounded-full ${s.cls}`}
      data-testid="wallet-status-badge"
    >
      {s.label}
    </span>
  );
}

export default function WalletBilling() {
  const { refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [savingCountry, setSavingCountry] = useState(false);
  const [tx, setTx] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [invFrom, setInvFrom] = useState("");
  const [invTo, setInvTo] = useState("");
  const [tiersOpen, setTiersOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, txs] = await Promise.all([
        creditsStatus(),
        creditsTransactions().catch(() => []),
      ]);
      setData(st);
      setTx(txs || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await creditsTransactionsCsv();
      downloadBlob(res.data, filenameFromResponse(res, "BeSca_Kontoauszug.csv"));
    } catch {
      toast.error("Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  };

  const downloadInvoice = async (number) => {
    try {
      const res = await creditsInvoicePdf(number);
      downloadBlob(res.data, `Rechnung_${number}.pdf`);
    } catch {
      toast.error("Rechnung konnte nicht geladen werden.");
    }
  };

  const exportZip = async () => {
    setZipping(true);
    try {
      const params = {};
      if (invFrom) params.from = invFrom;
      if (invTo) params.to = invTo;
      const res = await creditsInvoicesZip(params);
      downloadBlob(res.data, "BeSca_Rechnungen.zip");
    } catch {
      toast.error("ZIP-Export fehlgeschlagen.");
    } finally {
      setZipping(false);
    }
  };

  const topup = params.get("topup");
  const handleSuccess = useCallback(async () => {
    const sid = params.get("session_id");
    if (sid) {
      try {
        await creditsSession(sid);
      } catch {
        /* webhook reconciles shortly */
      }
    }
    await Promise.all([load(), refresh()]);
    toast.success("Aufladung erfolgreich – Ihr Guthaben wurde gutgeschrieben.");
    setParams({}, { replace: true });
  }, [params, load, refresh, setParams]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (topup === "success") handleSuccess();
    else if (topup === "cancel") {
      toast.info("Aufladung abgebrochen.");
      setParams({}, { replace: true });
    }
  }, [topup, handleSuccess, setParams]);

  const setupParam = params.get("setup");
  const handleSetup = useCallback(async () => {
    const sid = params.get("session_id");
    if (sid) {
      try {
        await autoRechargeConfirm(sid);
      } catch {
        /* webhook reconciles */
      }
    }
    await load();
    toast.success("Karte hinterlegt. Sie können die automatische Aufladung jetzt aktivieren.");
    setParams({}, { replace: true });
  }, [params, load, setParams]);

  useEffect(() => {
    if (setupParam === "success") handleSetup();
    else if (setupParam === "cancel") {
      toast.info("Hinterlegen der Karte abgebrochen.");
      setParams({}, { replace: true });
    }
  }, [setupParam, handleSetup, setParams]);

  const charge = async (pkg) => {
    setBusy(pkg);
    try {
      const { url } = await creditsCheckout(pkg, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aufladung konnte nicht gestartet werden.");
      setBusy("");
    }
  };

  const changeCountry = async (c) => {
    setSavingCountry(true);
    try {
      await creditsSetCountry(c);
      setData(await creditsStatus());
      toast.success("Steuerland aktualisiert.");
    } catch {
      toast.error("Land konnte nicht gespeichert werden.");
    } finally {
      setSavingCountry(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-12" data-testid="wallet-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (!data) return null;

  const balance = Number(data.wallet_balance) || 0;
  const state = data.billing_state;
  const blocked = state === "blocked";
  const inTrial = state === "trial";
  const low = state === "active" && balance > 0 && balance < (data.low_balance_threshold || 10);
  const rc = !!data.reverse_charge;
  const fc = data.forecast_detail || {};
  const trialDays = data.org_trial_ends_at
    ? Math.max(0, Math.ceil((new Date(data.org_trial_ends_at) - new Date()) / 86400000))
    : 0;
  const totalUsers = fc.total_users || 0;
  const isStb = fc.model === "stb";

  const planTiers = data.plan_tiers || [];
  const planMin = data.plan_tier_min || 0;
  const planUnit = data.plan_tier_unit || "Konten";
  const planUnitSing = planUnit === "Konten" ? "Konto" : "Mitarbeiter";
  const planRows = (() => {
    let prev = 0;
    return planTiers.map((t, i) => {
      const lower = prev + 1;
      const upper = t.upto;
      prev = upper == null ? prev : upper;
      let range;
      if (i === 0) range = `bis ${upper} ${planUnit}`;
      else if (upper == null) range = `ab ${lower} ${planUnit}`;
      else range = `${lower}–${upper} ${planUnit}`;
      return { range, price: t.price, isMin: i === 0 };
    });
  })();

  return (
    <div className="space-y-6" data-testid="wallet-billing">
      {/* Guthaben + Status */}
      <div className="rounded-lg border border-border bg-white p-6" data-testid="wallet-balance-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
              <Wallet className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="data-label text-muted-foreground">Aktuelles Guthaben</p>
              <p
                className="font-heading text-3xl font-semibold tabular"
                data-testid="wallet-balance"
              >
                {credits(balance)}
              </p>
            </div>
          </div>
          <StatusBadge state={state} />
        </div>

        {inTrial && (
          <div
            className="mt-4 flex items-start gap-2 text-sm rounded-md bg-primary/5 border border-border p-3"
            data-testid="wallet-trial"
          >
            <CalendarClock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-muted-foreground">
              Kostenlose Testphase – noch <strong>{trialDays}</strong>{" "}
              {trialDays === 1 ? "Tag" : "Tage"} (bis {fmtDate(data.org_trial_ends_at)}). Gratis
              enthalten: bis zu <strong>{data.free_mandanten}</strong> Mandanten und{" "}
              <strong>{data.free_mitarbeiter}</strong> Mitarbeiter.
            </p>
          </div>
        )}

        {blocked && (
          <div
            className="mt-4 flex items-start gap-2 text-sm rounded-md bg-destructive/10 border border-destructive p-3 text-destructive"
            data-testid="wallet-blocked"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              Ihr Guthaben ist aufgebraucht. Vorhandene Belege bleiben einseh- und exportierbar,
              aber es können keine neuen Belege oder Konten angelegt werden. Bitte laden Sie Ihr
              Guthaben auf.
            </p>
          </div>
        )}

        {low && (
          <div
            className="mt-4 flex items-start gap-2 text-sm rounded-md bg-amber-50 border border-amber-300 p-3 text-amber-800"
            data-testid="wallet-low"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              Ihr Guthaben ist niedrig ({credits(balance)}). Laden Sie rechtzeitig auf, um eine
              Unterbrechung zu vermeiden.
            </p>
          </div>
        )}
      </div>

      {/* Kostenprognose */}
      <div className="rounded-lg border border-border bg-white p-6" data-testid="wallet-forecast-card">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="font-medium">Kostenprognose (laufender Monat)</h2>
        </div>
        {totalUsers === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="forecast-empty">
            Derzeit keine abrechenbaren Konten. Sobald aktive{" "}
            {isStb ? "Mandanten, Mitarbeiter oder Kanzlei-Mitarbeiter" : "Mitarbeiter"} hinzukommen,
            erscheint hier Ihre voraussichtliche Monatsabrechnung.
          </p>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between" data-testid="forecast-line">
              <span className="text-muted-foreground">
                {totalUsers} {isStb ? (totalUsers === 1 ? "Konto" : "Konten") : "Mitarbeiter"}{" "}
                (abgerechnet: {fc.units} × {eur(fc.price)})
              </span>
              <span className="font-medium tabular">{eur(fc.amount)}</span>
            </div>
            {isStb && (
              <p className="text-xs text-muted-foreground" data-testid="forecast-breakdown">
                davon {fc.mandanten} Mandant{fc.mandanten === 1 ? "" : "en"}, {fc.mitarbeiter}{" "}
                Mitarbeiter, {fc.kanzlei} Kanzlei-Mitarbeiter
              </p>
            )}
            <div className="flex justify-between border-t border-border pt-2">
              <span className="font-medium">Voraussichtlich / Monat</span>
              <span className="font-semibold tabular" data-testid="forecast-total">
                {eur(data.monthly_forecast)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Staffelpreise (aufklappbar) */}
      <div className="rounded-lg border border-border bg-white" data-testid="wallet-tiers-card">
        <button
          type="button"
          onClick={() => setTiersOpen((o) => !o)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
          data-testid="wallet-tiers-toggle"
        >
          <Layers className="h-5 w-5 text-primary shrink-0" />
          <h2 className="font-medium">
            Staffelpreise – {isStb ? "Steuerberater" : "Unternehmer"}
          </h2>
          {tiersOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
          )}
        </button>
        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-out ${
            tiersOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            {tiersOpen && (
              <div className="border-t border-border p-4">
                <div className="divide-y divide-border text-sm">
                  {planRows.map((r) => (
                    <div
                      key={r.range}
                      className="flex justify-between py-2 gap-4"
                      data-testid="tier-row"
                    >
                      <span className="text-muted-foreground">{r.range}</span>
                      <span className="font-medium tabular text-right">
                        {r.isMin
                          ? `Mindestpreis ${eur(planMin * r.price)} / Monat`
                          : `${eur(r.price)} je ${planUnitSing}`}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {isStb
                    ? "Berechnet auf die Gesamtzahl aller Konten (Mandanten, deren Mitarbeiter und Kanzlei-Mitarbeiter)."
                    : "Berechnet auf die Gesamtzahl Ihrer Mitarbeiter."}{" "}
                  Der jeweils gültige Staffelpreis gilt für alle {planUnit}.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 1) Guthaben aufladen */}
      <CollapsibleCard
        icon={Coins}
        title="Guthaben aufladen"
        testid="wallet-packages"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data.packages || []).map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-border bg-white p-5 flex flex-col items-center text-center"
              data-testid={`package-${p.id}`}
            >
              <p className="font-heading text-2xl font-semibold tabular">{credits(p.net)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {eur(p.net)} netto{p.reverse_charge ? " · Reverse Charge" : ""}
              </p>
              <Button
                className="mt-4 w-full"
                onClick={() => charge(p.id)}
                disabled={!!busy}
                data-testid={`charge-${p.id}-button`}
              >
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aufladen"}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Zahlung sicher über Stripe (Kreditkarte/Klarna). Gutgeschrieben wird der Nettobetrag; das
          Guthaben wird sofort verbucht.
        </p>
      </CollapsibleCard>

      {/* 2) Steuerland */}
      <CollapsibleCard
        icon={Globe}
        title="Steuerland"
        titleRight={data.country_labels?.[data.billing_country] || data.billing_country}
        testid="wallet-country-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {rc
              ? "Reverse Charge – Steuerschuldnerschaft des Leistungsempfängers (keine MwSt.)."
              : "Österreich – 20 % Umsatzsteuer wird auf Aufladungen berechnet."}
          </p>
          <div className="w-full sm:w-56">
            <Select
              value={data.billing_country}
              onValueChange={changeCountry}
              disabled={savingCountry}
            >
              <SelectTrigger className="bg-white" data-testid="country-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_ORDER.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`country-${c}`}>
                    {data.country_labels?.[c] || c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CollapsibleCard>

      {/* 3) Automatisches Aufladen */}
      <AutoRecharge data={data} onSaved={load} />

      {/* 4) Rechnungsverlauf */}
      <CollapsibleCard
        icon={ReceiptText}
        title="Rechnungsverlauf"
        testid="wallet-transactions-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
          <div>
            <span className="data-label text-muted-foreground">Von</span>
            <Input
              type="date"
              value={invFrom}
              onChange={(e) => setInvFrom(e.target.value)}
              className="bg-white mt-1 w-full sm:w-44"
              data-testid="invoices-from"
            />
          </div>
          <div>
            <span className="data-label text-muted-foreground">Bis</span>
            <Input
              type="date"
              value={invTo}
              onChange={(e) => setInvTo(e.target.value)}
              className="bg-white mt-1 w-full sm:w-44"
              data-testid="invoices-to"
            />
          </div>
          <div className="flex gap-2 sm:ml-auto">
            <Button
              size="sm"
              variant="secondary"
              className="border border-border gap-2"
              onClick={exportCsv}
              disabled={exporting || tx.length === 0}
              data-testid="transactions-export-csv"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              CSV
            </Button>
            <Button
              size="sm"
              className="gap-2"
              onClick={exportZip}
              disabled={zipping || tx.length === 0}
              data-testid="invoices-export-zip"
            >
              {zipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              CSV + PDFs (ZIP)
            </Button>
          </div>
        </div>
        {tx.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="transactions-empty">
            Noch keine Transaktionen. Aufladungen und monatliche Abbuchungen erscheinen hier.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left font-medium py-2 pr-4 whitespace-nowrap">Datum</th>
                  <th className="text-left font-medium py-2 pr-4 whitespace-nowrap">Typ</th>
                  <th className="text-left font-medium py-2 pr-4">Beschreibung</th>
                  <th className="text-right font-medium py-2 pr-4 whitespace-nowrap">Betrag</th>
                  <th className="text-right font-medium py-2 pr-4 whitespace-nowrap">Guthaben</th>
                  <th className="text-right font-medium py-2 whitespace-nowrap">Rechnung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tx.map((t) => {
                  const credit = t.type === "topup" || t.type === "auto_topup";
                  return (
                    <tr key={t.id} data-testid="transaction-row" className="align-top">
                      <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                        {fmtDateTime(t.ts)}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap font-medium">
                        {TX_LABEL[t.type] || t.type}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">{t.detail || "—"}</td>
                      <td
                        className={`py-2 pr-4 text-right tabular font-medium ${
                          credit ? "text-emerald-600" : "text-destructive"
                        }`}
                      >
                        {credit ? "+" : "−"}
                        {credits(Math.abs(Number(t.amount) || 0))}
                      </td>
                      <td className="py-2 pr-4 text-right tabular text-muted-foreground">
                        {credits(t.balance_after)}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {t.invoice_number ? (
                          <button
                            type="button"
                            onClick={() => downloadInvoice(t.invoice_number)}
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                            data-testid={`invoice-download-${t.invoice_number}`}
                            title={`Rechnung ${t.invoice_number} herunterladen`}
                          >
                            <FileDown className="h-3.5 w-3.5" />
                            {t.invoice_number}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleCard>
    </div>
  );
}
